export const ADMIN_PERMISSIONS = {
  all: '*',
  commerceCreditsView: 'commerce.credits.view',
  commerceCreditsRead: 'commerce.credits.read',
  commerceCreditsWrite: 'commerce.credits.write',
  commerceCostsView: 'commerce.costs.view',
  commerceCostsRead: 'commerce.costs.read',
  commerceOrdersRead: 'commerce.orders.read',
  commerceOrdersRefund: 'commerce.orders.refund',
  gatewayPrincipalsView: 'gateway_principals.view',
  gatewayPrincipalsRead: 'gateway_principals.read',
  gatewayPrincipalsWrite: 'gateway_principals.write',
  pricingView: 'pricing.view',
  pricingRead: 'pricing.read',
  pricingWrite: 'pricing.write',
  llmRoutesView: 'llm_routes.view',
  llmRoutesRead: 'llm_routes.read',
  llmRoutesWrite: 'llm_routes.write',
  llmAccessView: 'llm_access.view',
  llmAccessRead: 'llm_access.read',
  llmAccessWrite: 'llm_access.write',
  alertAttributionView: 'alert_attribution.view',
  alertAttributionRead: 'alert_attribution.read',
  alertAttributionSensitive: 'alert_attribution.sensitive',
  desktopUpdatesView: 'desktop_updates.view',
  desktopUpdatesRead: 'desktop_updates.read',
  desktopUpdatesWrite: 'desktop_updates.write',
  sessionLogsView: 'session_logs.view',
  sessionLogsRead: 'session_logs.read',
  sessionLogsWrite: 'session_logs.write',
  taskPlatformView: 'task_platform.view',
  taskPlatformRead: 'task_platform.read',
} as const;

export type AdminPermission =
  (typeof ADMIN_PERMISSIONS)[keyof typeof ADMIN_PERMISSIONS];

export interface AdminPermissionState {
  isAdmin: boolean;
  permissionsEnabled: boolean;
  permissions: readonly string[];
}

export function hasAdminPermission(
  state: AdminPermissionState,
  requiredPermission: AdminPermission,
): boolean {
  if (!state.isAdmin) {
    return false;
  }
  if (requiredPermission === ADMIN_PERMISSIONS.sessionLogsView) {
    return true;
  }
  if (!state.permissionsEnabled) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.all) ||
    state.permissions.includes(requiredPermission)
  ) {
    return true;
  }
  // Commerce credits: write implies read implies view.
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.commerceCreditsWrite) &&
    (requiredPermission === ADMIN_PERMISSIONS.commerceCreditsRead ||
      requiredPermission === ADMIN_PERMISSIONS.commerceCreditsView)
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.commerceCreditsRead) &&
    requiredPermission === ADMIN_PERMISSIONS.commerceCreditsView
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.commerceCostsRead) &&
    requiredPermission === ADMIN_PERMISSIONS.commerceCostsView
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.commerceOrdersRefund) &&
    requiredPermission === ADMIN_PERMISSIONS.commerceOrdersRead
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.gatewayPrincipalsWrite) &&
    (requiredPermission === ADMIN_PERMISSIONS.gatewayPrincipalsRead ||
      requiredPermission === ADMIN_PERMISSIONS.gatewayPrincipalsView)
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.gatewayPrincipalsRead) &&
    requiredPermission === ADMIN_PERMISSIONS.gatewayPrincipalsView
  ) {
    return true;
  }
  // Pricing: write implies read implies view (mirrors commerce).
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.pricingWrite) &&
    (requiredPermission === ADMIN_PERMISSIONS.pricingRead ||
      requiredPermission === ADMIN_PERMISSIONS.pricingView)
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.pricingRead) &&
    requiredPermission === ADMIN_PERMISSIONS.pricingView
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.llmRoutesWrite) &&
    (requiredPermission === ADMIN_PERMISSIONS.llmRoutesRead ||
      requiredPermission === ADMIN_PERMISSIONS.llmRoutesView)
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.llmRoutesRead) &&
    requiredPermission === ADMIN_PERMISSIONS.llmRoutesView
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.llmAccessWrite) &&
    (requiredPermission === ADMIN_PERMISSIONS.llmAccessRead ||
      requiredPermission === ADMIN_PERMISSIONS.llmAccessView)
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.llmAccessRead) &&
    requiredPermission === ADMIN_PERMISSIONS.llmAccessView
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.alertAttributionSensitive) &&
    (requiredPermission === ADMIN_PERMISSIONS.alertAttributionRead ||
      requiredPermission === ADMIN_PERMISSIONS.alertAttributionView)
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.alertAttributionRead) &&
    requiredPermission === ADMIN_PERMISSIONS.alertAttributionView
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.desktopUpdatesWrite) &&
    (requiredPermission === ADMIN_PERMISSIONS.desktopUpdatesRead ||
      requiredPermission === ADMIN_PERMISSIONS.desktopUpdatesView)
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.desktopUpdatesRead) &&
    requiredPermission === ADMIN_PERMISSIONS.desktopUpdatesView
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.taskPlatformRead) &&
    requiredPermission === ADMIN_PERMISSIONS.taskPlatformView
  ) {
    return true;
  }
  if (
    state.permissions.includes(ADMIN_PERMISSIONS.sessionLogsWrite) &&
    requiredPermission === ADMIN_PERMISSIONS.sessionLogsRead
  ) {
    return true;
  }
  return false;
}
